<template>
  <router-view v-slot="{ Component, route }">
    <transition 
      :name="transitionName" 
      mode="out-in" 
      @before-enter="handleBeforeEnter" 
      @after-enter="handleAfterEnter"
      @before-leave="handleBeforeLeave"
      @after-leave="handleAfterLeave"
    >
      <component :is="Component" :key="route.path" />
    </transition>
  </router-view>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { connectUserWs, disconnectUserWs } from './ws/userWs'

export default {
  name: 'App',
  setup() {
    const router = useRouter()
    const transitionName = ref('page-fade')

    const handleKick = (msg) => {
      // 被禁用/强制下线：清理用户态并跳转登录
      localStorage.removeItem('user_token')
      localStorage.removeItem('user_profile')
      disconnectUserWs()
      const reason = (msg && msg.reason) || '你已被强制下线'
      router.replace({ path: '/login', query: { reason } })
    }

    // 页面切换动画
    const lockScroll = () => {
      // 切换动画期间锁定滚动，避免滚动条闪现
      document.documentElement.classList.add('mc-route-lock')
      document.body.classList.add('mc-route-lock')

      // 兜底：部分浏览器在 class 生效前仍可能闪一下
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      document.body.style.paddingRight = '0'

      try {
        const app = document.getElementById('app')
        if (app) app.classList.add('mc-route-lock')
      } catch (e) {
        // ignore
      }
    }

    const unlockScroll = () => {
      // 动画结束后恢复滚动（建筑详情页等需要滚动）
      document.documentElement.classList.remove('mc-route-lock')
      document.body.classList.remove('mc-route-lock')

      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.paddingRight = ''

      try {
        const app = document.getElementById('app')
        if (app) app.classList.remove('mc-route-lock')
      } catch (e) {
        // ignore
      }
    }

    const handleBeforeEnter = () => {
      lockScroll()
    }

    const handleAfterEnter = () => {
      unlockScroll()
    }

    const handleBeforeLeave = () => {
      lockScroll()
    }

    const handleAfterLeave = () => {
      // after-leave 也恢复一次，防止某些情况下 after-enter 不触发
      unlockScroll()
    }

    onMounted(() => {
      connectUserWs({
        getToken: () => localStorage.getItem('user_token'),
        onKick: handleKick
      })

      // 监听路由变化，设置动画方向
      router.beforeEach((to, from, next) => {
        // 检查是否禁用动画的路由
        const noAnimationRoutes = ['/building-snapshot/:id']
        const isNoAnimation = noAnimationRoutes.some(r => {
          if (r.includes(':')) {
            const pattern = r.replace(/:id/gi, '[^/]+')
            return new RegExp(`^${pattern}$`).test(to.path)
          }
          return to.path === r
        })

        if (isNoAnimation) {
          transitionName.value = 'none'
        } else {
          // 根据路由深度决定动画方向
          const toDepth = to.path.split('/').length
          const fromDepth = from.path.split('/').length
          transitionName.value = toDepth < fromDepth ? 'slide-right' : 'slide-left'
        }

        next()
      })
    })

    onUnmounted(() => {
      disconnectUserWs()
    })

    return {
      transitionName,
      handleBeforeEnter,
      handleAfterEnter,
      handleBeforeLeave,
      handleAfterLeave
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

html, body {
  overflow-x: hidden;
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: #5ea818 #0b0b0b;
  -webkit-text-size-adjust: 100%;
  background-color: #0b0b0b;
  min-height: 100vh;
  min-height: -webkit-fill-available;
}

#app {
  width: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

input, button, textarea {
  font-family: inherit;
  font-size: 16px;
}

img {
  max-width: 100%;
  height: auto;
}

@supports (padding: max(0px)) {
  body {
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
    padding-bottom: env(safe-area-inset-bottom);
  }
}

@media (max-width: 768px) {
  html, body {
    scrollbar-width: none;
  }

  html::-webkit-scrollbar,
  body::-webkit-scrollbar {
    display: none;
  }
}

html::-webkit-scrollbar,
body::-webkit-scrollbar {
  width: 16px;
}

html::-webkit-scrollbar-track,
body::-webkit-scrollbar-track {
  background: linear-gradient(#0b0b0b, #111);
  border-left: 4px solid #000;
  box-shadow: inset 4px 0 0 rgba(255, 255, 255, 0.04);
}

html::-webkit-scrollbar-thumb,
body::-webkit-scrollbar-thumb {
  background:
    linear-gradient(90deg, #2f7d1b 0 25%, #3e8f27 25% 50%, #2f7d1b 50% 75%, #3e8f27 75% 100%);
  border: 4px solid #000;
  box-shadow: inset -3px -3px 0 rgba(0,0,0,0.35), inset 3px 3px 0 rgba(255,255,255,0.08);
}

html::-webkit-scrollbar-thumb:hover,
body::-webkit-scrollbar-thumb:hover {
  background:
    linear-gradient(90deg, #3e8f27 0 25%, #59b13b 25% 50%, #3e8f27 50% 75%, #59b13b 75% 100%);
}

html::-webkit-scrollbar-thumb:active,
body::-webkit-scrollbar-thumb:active {
  background:
    linear-gradient(90deg, #245f14 0 25%, #2f7d1b 25% 50%, #245f14 50% 75%, #2f7d1b 75% 100%);
}

html::-webkit-scrollbar-button:single-button,
body::-webkit-scrollbar-button:single-button {
  display: block;
  height: 16px;
  width: 16px;
  background: #101010;
  border-left: 4px solid #000;
}

html::-webkit-scrollbar-button:single-button:vertical:decrement,
body::-webkit-scrollbar-button:single-button:vertical:decrement {
  background:
    linear-gradient(#101010, #0b0b0b),
    conic-gradient(from 225deg at 50% 60%, #9ae054 0 25%, transparent 0 100%);
}

html::-webkit-scrollbar-button:single-button:vertical:increment,
body::-webkit-scrollbar-button:single-button:vertical:increment {
  background:
    linear-gradient(#101010, #0b0b0b),
    conic-gradient(from 45deg at 50% 40%, #9ae054 0 25%, transparent 0 100%);
}

/* 页面切换动画期间：彻底禁止滚动条闪现 */
html.mc-route-lock,
body.mc-route-lock {
  overflow: hidden !important;
}

html.mc-route-lock::-webkit-scrollbar,
body.mc-route-lock::-webkit-scrollbar {
  display: none !important;
}

#app.mc-route-lock {
  height: 100vh;
  overflow: hidden !important;
}

#app {
  width: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
}

/* 页面切换动画 - 淡入淡出 + 缩放 */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-fade-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: scale(1.05) translateY(-20px);
}

.page-fade-enter-to,
.page-fade-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* 页面切换动画 - 左滑进入 */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(60px) scale(0.95);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-60px) scale(1.05);
}

.slide-left-enter-to,
.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}

/* 页面切换动画 - 右滑进入（后退） */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-60px) scale(0.95);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translateX(60px) scale(1.05);
}

.slide-right-enter-to,
.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}

/* 无动画 - 用于特殊页面 */
.none-enter-active,
.none-leave-active {
  transition: none;
}

.none-enter-from,
.none-leave-to,
.none-enter-to,
.none-leave-from {
  opacity: 1;
  transform: none;
}

/* 减少动画效果 - 尊重用户偏好 */
@media (prefers-reduced-motion: reduce) {
  .page-fade-enter-active,
  .page-fade-leave-active,
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active {
    transition: opacity 0.2s ease;
  }

  .page-fade-enter-from,
  .page-fade-leave-to,
  .slide-left-enter-from,
  .slide-left-leave-to,
  .slide-right-enter-from,
  .slide-right-leave-to {
    transform: none;
  }
}
</style>
